/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* gebruikeredit.ts
* 16-12-2019 - Jelmer Jellema - Spin in het Web B.V.
*/

import {Component} from '@angular/core';
import {Editmodelscherm} from '../abstracts/editmodelscherm';
import {Gebruiker} from '../../models/gebruiker';
import {GebruikerWerkmaatschappij} from '../../models/gebruiker_werkmaatschappij';
import {Werkmaatschappij} from '../../models/werkmaatschappij';
import {Groep} from '../../models/groep';
import {hash} from '../../app/types';
import {Datarecord} from '../../services/api/api.service';
import {Leverancier} from '../../models/leverancier';
import {GebruikerProject} from '../../models/gebruiker_project';
import {Project} from '../../models/project';
import {DateTime} from 'luxon';
import {Basemodel} from '../../models/basemodel';

@Component({
  selector: 'gebruiker-edit',
  templateUrl: './gebruikeredit.html',
  // styleUrls: ['./gebruikeredit.scss']
})
export class Gebruikeredit extends Editmodelscherm<typeof Gebruiker> {
  _starttitle = 'Gebruiker';
  icon = 'fas fa-user';
  defaultRecord = {
    gebruiker_werkmaatschappij: [],
    gebruiker_project: []
  };
  model = Gebruiker;
  defaultSubmodels = [GebruikerWerkmaatschappij, GebruikerProject];
  lookupQueries = {
    groep: {
      model: Groep,
      aclModel: Gebruiker,
    },
    werkmaatschappij: {
      model: Werkmaatschappij,
      aclModel: Gebruiker
    },
    leverancier: {
      model: Leverancier,
      aclModel: Gebruiker
    },
    project: {
      model: Project,
      aclModel: Gebruiker,
      suboptions: [
        [Project,
          {
            where: {
              einddatum: {
                $or: [
                  {
                    $gte: DateTime.now().toSQLDate()
                  },
                  null
                ]
              }
            }
          }
        ] as [typeof Basemodel, hash<any>]
      ],
      sortBy: 'naam'
    }
  };
  //gecaste queries
  groephash: hash<Groep> = {};

  //constructor ivm injection
  constructor() {
    super('debug');
  }

  /**
   * Dynamische titel
   */
  get title(): string {
    return this.isNieuw ? 'Nieuwe gebruiker' : `${this.record?.orig('naam')} - Bewerk gebruiker`;
  }

  //recordbeschrijving
  get recordBeschrijving(): string {
    return this.isNieuw ? 'een nieuwe gebruiker' : `de gebruiker '${this.record?.orig('naam')}'`;
  }

  /**
   * Overwrite van de gewone test op can(table,'full');
   * Alleen toegang als je superadmin bent:
   */
  async canWrite(): Promise<boolean> {
    return this.api.userdata.app_admin;
  }

  protected afterHaalLookups() {
    super.afterHaalLookups();
    //we casten de groepen
    this.groephash = {};
    this.groephash = <hash<Groep>> this.lookupHashes.groep;

    //en de werkmaatschappij, inclusief wel of niet lid
    for (let gwm of this.record.gebruiker_werkmaatschappij) {
      this.lookupHashes.werkmaatschappij[gwm.werkmaatschappij_id]._tags.select = true;
    }

    //projectselectie
    this.log.debug(`Projectselectie: gebruiker_project`,this.record.gebruiker_project);
    for (let gp of this.record.gebruiker_project) {
      if (this.lookupHashes.project[gp.project_id]) {
        this.lookupHashes.project[gp.project_id]._tags.select = true;
      }
    }
  }

  //typecasts van lookups
  get projecten(): Project[] {
    return <Project[]> this.lookups.project;
  }

  get werkmaatschappijen(): Werkmaatschappij[] {
    return <Werkmaatschappij[]> this.lookups.werkmaatschappij;
  }

  /**
   * ux helper bij selectie leveranciers
   */

  leveranciersInWM(): Leverancier[] {
    return (<Leverancier[]> this.lookups.leverancier || []).filter(l => this.lookupHashes.werkmaatschappij[l.werkmaatschappij_id]._tags.select);
  }

  /**
   * Ux helper bij wel of niet tonen leverancier
   */
  get isLeverancierAccount(): boolean {
    return this.record?.groep_id && this.lookupHashes.groep && (<Groep> this.lookupHashes.groep[this.record.groep_id])?.groep === 'leverancier';
  }

  /**
   * Helper bij checken opslaan en bij save
   */
  get geselecteerdeWerkmaatschappijen(): Werkmaatschappij[] {
    return this.werkmaatschappijen.filter(wm => wm._tags.select);
  }

  isGeselecteerdeWM(wmid: number): boolean {
    return this.lookupHashes.werkmaatschappij[wmid]._tags.select;
  }

  /**
   * Geeft true als er een groep is geselecteerd met projectselectie
   */
  get projectSelectie(): boolean {
    let acl = this.record?.groep_id && this.lookupHashes.groep && (<Groep> this.lookupHashes.groep[this.record.groep_id])?.acl;
    return acl && /(?:^|\s)planitem:(project|readproject)(?:$|\s)/.test(acl);
  }

  protected savedata(): Datarecord {
    //welke werkmaatschappijen?
    let wms = this.geselecteerdeWerkmaatschappijen;
    for (let wm of wms) {
      if (!this.record.gebruiker_werkmaatschappij.some(gwm => gwm.werkmaatschappij_id === wm.id)) {
        this.record.gebruiker_werkmaatschappij.push(GebruikerWerkmaatschappij.create({werkmaatschappij_id: wm.id})); //gebruiker_id wordt wel gezet door het backend
      }
    }
    //en andersom
    this.record.gebruiker_werkmaatschappij = this.record.gebruiker_werkmaatschappij.filter(gwm => wms.some(wm => wm.id === gwm.werkmaatschappij_id));

    //welke projecten? Als er geen projectselectie is, laten we de oude staan. Die staan dan niet in de weg, en handig om fouten te herstellen
    if (this.projectSelectie) {
      this.record.gebruiker_project = this.projecten.filter(p => p._tags.select).map(p => GebruikerProject.create({project_id: p.id}));
    }
    return super.savedata();
  }

}
